"use client";

import { cn } from "@/lib/utils";

interface AuraPresenceProps {
  size?: "sm" | "md" | "lg";
  intensity?: "subtle" | "normal" | "strong";
  className?: string;
}

export function AuraPresence({
  size = "md",
  intensity = "normal",
  className,
}: AuraPresenceProps) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-24 h-24",
  };

  const opacityClasses = {
    subtle: "opacity-40",
    normal: "opacity-60",
    strong: "opacity-80",
  };

  return (
    <div
      className={cn(
        "relative flex items-center justify-center",
        sizeClasses[size],
        className
      )}
    >
      {/* Outer glow ring */}
      <div
        className={cn(
          "absolute inset-0 rounded-full aura-breathe",
          "bg-gradient-to-br from-accent/20 via-accent/10 to-transparent",
          "blur-md",
          opacityClasses[intensity]
        )}
      />

      {/* Middle ring */}
      <div
        className={cn(
          "absolute inset-1 rounded-full aura-glow",
          "bg-gradient-to-tr from-accent/30 via-transparent to-accent/20",
          "blur-sm"
        )}
      />

      {/* Inner core */}
      <div
        className={cn(
          "absolute rounded-full",
          size === "sm" ? "inset-2" : size === "md" ? "inset-3" : "inset-6",
          "bg-gradient-to-br from-accent/50 via-accent/30 to-accent/10",
          "backdrop-blur-sm",
          "border border-accent/30"
        )}
      />

      {/* Central luminous point */}
      <div
        className={cn(
          "absolute rounded-full aura-pulse",
          size === "sm" ? "w-1.5 h-1.5" : size === "md" ? "w-2 h-2" : "w-4 h-4",
          "bg-accent/80",
          "shadow-[0_0_12px_4px] shadow-accent/40"
        )}
      />
    </div>
  );
}
